/**
 * Potential energy and energy conservation.
 *
 *   U = m g h                    gravitational potential energy near a planet's surface
 *   U = ½ k x²                   energy stored in a spring stretched or compressed by x
 *   v² = u² + 2 g h              speed after the object loses height h (no friction, no drag)
 *   ½ k x² = ½ m v²              a spring's stored energy becoming kinetic energy
 *
 * OpenStax University Physics Vol. 1, §7.3 (conservative forces and potential energy) and
 * §8.2 (conservation of energy). Heights and compressions are measured from a reference
 * the reader chooses: only differences in U have physical meaning.
 */

import type {
  RelationDef,
  RelationError,
} from "@/lib/tool-kit/physics-mechanics/relation";
import {
  ACCELERATION,
  ENERGY,
  LENGTH,
  MASS,
  SPEED,
  SPRING_CONSTANT,
} from "@/lib/tool-kit/physics-mechanics/units";

const fail = (error: string): RelationError => ({ error });

export type GravityEnergyKey = "U" | "m" | "g" | "h";

export const GRAVITY_ENERGY: RelationDef<GravityEnergyKey> = {
  keys: ["U", "m", "g", "h"],
  symbols: { U: "PE", m: "m", g: "g", h: "h" },
  families: { U: ENERGY, m: MASS, g: ACCELERATION, h: LENGTH },
  equation: "PE = m × g × h",
  solvedFor: "U",
  rearranged: {
    U: "PE = m × g × h",
    m: "m = PE ÷ (g × h)",
    g: "g = PE ÷ (m × h)",
    h: "h = PE ÷ (m × g)",
  },
  solve(unknown, { U, m, g, h }) {
    if (unknown !== "m" && !(m > 0)) {
      return fail("mass-not-positive");
    }
    if (unknown !== "g" && !(g > 0)) {
      return fail("gravity-not-positive");
    }
    if (unknown === "U") {
      return m * g * h;
    }
    if (unknown === "h") {
      return U / (m * g);
    }
    if (h === 0) {
      return fail("height-zero");
    }
    return unknown === "m" ? U / (g * h) : U / (m * h);
  },
};

export type SpringEnergyKey = "U" | "k" | "x";

export const SPRING_ENERGY: RelationDef<SpringEnergyKey> = {
  keys: ["U", "k", "x"],
  symbols: { U: "PE", k: "k", x: "x" },
  families: { U: ENERGY, k: SPRING_CONSTANT, x: LENGTH },
  equation: "PE = ½ × k × x²",
  solvedFor: "U",
  rearranged: {
    U: "PE = ½ × k × x²",
    k: "k = 2 × PE ÷ x²",
    x: "x = √(2 × PE ÷ k)",
  },
  solve(unknown, { U, k, x }) {
    if (unknown !== "U" && U < 0) {
      return fail("energy-negative");
    }
    if (unknown !== "k" && !(k > 0)) {
      return fail("stiffness-not-positive");
    }
    if (unknown === "U") {
      return 0.5 * k * x * x;
    }
    if (unknown === "k") {
      return x === 0 ? fail("stretch-zero") : (2 * U) / (x * x);
    }
    return Math.sqrt((2 * U) / k);
  },
};

export type DropKey = "v" | "u" | "h" | "g";

/** Energy conservation for a fall or a frictionless slide: v² = u² + 2gh, h being the drop. */
export const DROP: RelationDef<DropKey> = {
  keys: ["v", "u", "h", "g"],
  symbols: { v: "v", u: "u", h: "h", g: "g" },
  families: { v: SPEED, u: SPEED, h: LENGTH, g: ACCELERATION },
  equation: "v² = u² + 2 × g × h",
  solvedFor: "v",
  rearranged: {
    v: "v = √(u² + 2 × g × h)",
    u: "u = √(v² − 2 × g × h)",
    h: "h = (v² − u²) ÷ (2 × g)",
    g: "g = (v² − u²) ÷ (2 × h)",
  },
  solve(unknown, { v, u, h, g }) {
    if (unknown !== "g" && !(g > 0)) {
      return fail("gravity-not-positive");
    }
    if (unknown === "v" || unknown === "u") {
      const radicand = unknown === "v" ? u * u + 2 * g * h : v * v - 2 * g * h;
      return radicand < 0 ? fail("cannot-reach") : Math.sqrt(radicand);
    }
    if (unknown === "h") {
      return (v * v - u * u) / (2 * g);
    }
    if (h === 0) {
      return fail("height-zero");
    }
    const value = (v * v - u * u) / (2 * h);
    return value > 0 ? value : fail("gravity-not-positive");
  },
};

export type ReleaseKey = "v" | "k" | "x" | "m";

/** A spring's stored energy turning into motion: ½kx² = ½mv². */
export const RELEASE: RelationDef<ReleaseKey> = {
  keys: ["v", "k", "x", "m"],
  symbols: { v: "v", k: "k", x: "x", m: "m" },
  families: { v: SPEED, k: SPRING_CONSTANT, x: LENGTH, m: MASS },
  equation: "½ × k × x² = ½ × m × v²",
  solvedFor: "v",
  rearranged: {
    v: "v = x × √(k ÷ m)",
    k: "k = m × v² ÷ x²",
    x: "x = v × √(m ÷ k)",
    m: "m = k × x² ÷ v²",
  },
  solve(unknown, { v, k, x, m }) {
    if (unknown !== "k" && !(k > 0)) {
      return fail("stiffness-not-positive");
    }
    if (unknown !== "m" && !(m > 0)) {
      return fail("mass-not-positive");
    }
    if (unknown === "v") {
      return Math.abs(x) * Math.sqrt(k / m);
    }
    if (unknown === "x") {
      return Math.abs(v) * Math.sqrt(m / k);
    }
    if (unknown === "k") {
      return x === 0 ? fail("stretch-zero") : (m * v * v) / (x * x);
    }
    return v === 0 ? fail("speed-zero") : (k * x * x) / (v * v);
  },
};
